import { useEffect, useState } from "react";
import api, { euro, fmtDate, apiError, pctIt } from "@/lib/api";
import { SectionTitle, KpiCard } from "@/components/common";
import { Modal, Row, Field, Select, TextArea, PROVENIENZE } from "@/pages/Notizie";
import { EuroInput } from "@/components/EuroInput";
import { useFormErrors } from "@/lib/formErrors";
import { toast } from "sonner";
import { nominaFiles, mostraErroreFile } from "@/components/ConflittoNome";
import { ShieldCheck, Wallet, AlertTriangle, Plus, Check, Settings, Download, Target, Repeat, Pencil, Trash2, Pause, Play, Square, History, Undo2, TrendingUp, Paperclip } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Legend } from "recharts";
import { useAuth } from "@/context/AuthContext";
import FinanzaMese from "@/components/FinanzaMese";
import FinanzaAnno from "@/components/FinanzaAnno";
import FinanzaCestino from "@/components/FinanzaCestino";
import FinanzaProssimi from "@/components/FinanzaProssimi";
import { StoricoPulizia, RecuperoRipetute, CassaBanca, MovimentiPrevisti } from "@/components/FinanzaEstratto";
import { DeleteBtn } from "@/components/FinDelete";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SpeseMarketing } from "@/components/SpeseMarketing";
import { useSpesaAppr } from "@/components/RichiestaCampi";

const TABS = ["Dashboard", "Da pagare", "Spese", "Spese fisse", "Spese marketing", "Incassi", "Impostazioni"];
const TABS_SOCI = ["Mese", "Anno", "Prossimi incassi", "Spese fisse", "Spese marketing", "Cestino"];
const ALTRO = ["Dashboard", "Impostazioni"];
const FREQ = [["mensile", "Mensile"], ["bimestrale", "Bimestrale"], ["trimestrale", "Trimestrale"], ["semestrale", "Semestrale"], ["annuale", "Annuale"]];
const METODI = [["bonifico", "Bonifico"], ["carta", "Carta"], ["contanti", "Contanti"], ["RID", "RID"]];
const oggi = () => new Date().toISOString().slice(0, 10);
const meseOggi = () => new Date().toISOString().slice(0, 7);

export default function Finanza() {
  const { isDirezione: isSoci, isSegreteria } = useAuth();
  const isDirezione = isSoci || isSegreteria;
  const [tab, setTab] = useState(isDirezione ? "Mese" : "Dashboard");
  const [showExport, setShowExport] = useState(false);
  const [ver, setVer] = useState(0);
  const [sp] = useSearchParams();
  const [meseApri, setMeseApri] = useState(sp.get("mese") || null);
  const [altro, setAltro] = useState(false);
  const nav = useNavigate();
  const bump = () => setVer((v) => v + 1);
  const tabCls = (on) => `px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${on ? "border-[#FC82ED] text-[#111111]" : "border-transparent text-[var(--muted)] hover:text-[#111111]"}`;
  return (
    <div className="fade-up">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="text-[#FC82ED]" size={20} />
        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#FC82ED]">Riservato Direzione</span>
      </div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle sub="Ogni importo vive in un solo posto e si sposta automaticamente quando cambia stato.">Finanza Aziendale</SectionTitle>
        <button data-testid="export-btn" onClick={() => setShowExport(true)}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all"><Download size={15} /> Esporta</button>
      </div>
      {showExport && <ExportModal onClose={() => setShowExport(false)} />}
      <div className="flex gap-1 my-6 border-b border-[var(--border)] overflow-x-auto">
        {(isDirezione ? TABS_SOCI : TABS).map((t) => (
          <button key={t} data-testid={`fin-tab-${t.toLowerCase().replace(/ /g, "-")}`} onClick={() => { setTab(t); setAltro(false); }}
                  className={tabCls(tab === t)}>{t}</button>
        ))}
        {isDirezione && <button data-testid="fin-tab-altro" onClick={() => setAltro(!altro)} className={tabCls(ALTRO.includes(tab))}>Altro ▾</button>}
      </div>
      {isDirezione && altro && (
        <div className="-mt-4 mb-5 flex flex-wrap gap-2" data-testid="fin-altro-menu">
          {ALTRO.map((t) => <button key={t} data-testid={`fin-altro-${t.toLowerCase()}`} onClick={() => { setTab(t); setAltro(false); }} className="px-3 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-medium hover:border-[#FC82ED]">{t}</button>)}
          <button data-testid="fin-altro-provvigioni" onClick={() => nav("/direzione-provvigioni")} className="px-3 py-2 rounded-xl bg-white border border-[var(--border)] text-sm font-medium hover:border-[#FC82ED]">Provvigioni collaboratori · Fatture · Scadenze fiscali →</button>
        </div>
      )}
      {tab === "Mese" && isDirezione && <FinanzaMese key={`m${ver}${meseApri || ""}`} meseIniziale={meseApri} />}
      {tab === "Anno" && isDirezione && <FinanzaAnno onApriMese={(m) => { setMeseApri(m); setTab("Mese"); }} />}
      {tab === "Cestino" && isDirezione && <FinanzaCestino key={`t${ver}`} />}
      {tab === "Prossimi incassi" && isDirezione && <FinanzaProssimi key={`pi${ver}`} />}
      {tab === "Spese marketing" && <SpeseMarketing key={`mk${ver}`} />}
      {tab === "Dashboard" && <Dashboard key={`d${ver}`} />}
      {tab === "Da pagare" && <DaPagare key={`p${ver}`} onChange={bump} />}
      {tab === "Spese" && <Spese key={`s${ver}`} onChange={bump} />}
      {tab === "Spese fisse" && <SpeseFisse key={`f${ver}`} onChange={bump} />}
      {tab === "Incassi" && <Incassi key={`i${ver}`} onChange={bump} />}
      {tab === "Impostazioni" && <Impostazioni key={`c${ver}`} />}
    </div>
  );
}

// ---------- shared bits ----------
const giorniLabel = (g) => g == null ? "" : g < 0 ? `${-g} gg di ritardo` : g === 0 ? "oggi" : `tra ${g} gg`;
const giorniCls = (g) => g == null ? "text-[var(--muted)]" : g < 0 ? "text-red-600" : g <= 7 ? "text-amber-600" : "text-[var(--muted)]";

function useCats() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/finanza/categorie").then((r) => setCats(r.data.categorie || [])).catch(() => {}); }, []);
  return cats;
}

function AgenteSelect({ value, onChange, testid }) {
  const [ag, setAg] = useState([]);
  useEffect(() => { api.get("/finanza/agenti").then((r) => setAg(r.data || [])).catch(() => {}); }, []);
  return <Select label="Agente (costo diretto)" testid={testid} value={value || ""} onChange={onChange} opts={[["", "— nessuno (costo d'agenzia) —"], ...ag.map((a) => [a.id, a.nome])]} />;
}

async function uploadAllegato(movId, file, kind) {
  if (!file) return;
  const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
  try { await api.post(`/finanza/movimenti/${movId}/allegato`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
  catch (e) { toast.error("Allegato non caricato: " + apiError(e)); }
}

async function openAllegato(fid) {
  try {
    const res = await api.get(`/finanza/files/${fid}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { mostraErroreFile(e); }
}

const AllegatoBtn = ({ fid, label }) => fid ? (
  <button data-testid={`alleg-${fid}`} onClick={() => openAllegato(fid)} title={label}
          className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-[#FC82ED]"><Paperclip size={14} /></button>
) : null;

function FileInput({ label, testid, onPick }) {
  const [name, setName] = useState("");
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">{label}</label>
      <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-dashed border-[var(--border)] text-sm cursor-pointer hover:border-[#FC82ED]">
        <Paperclip size={14} className="text-[var(--muted)]" />
        <span className="truncate text-[var(--muted)]">{name || "Allega PDF o immagine (facoltativo)"}</span>
        <input data-testid={testid} type="file" className="hidden"
               onChange={async (e) => { const f0 = e.target.files[0] || null; e.target.value = ""; const [f] = f0 ? await nominaFiles([f0]) : []; if (f) { setName(f.name); onPick(f); } }} />
      </label>
    </div>
  );
}

function MovRow({ m, right }) {
  return (
    <div data-testid={`mov-row-${m.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border)] last:border-0 hover:bg-slate-50/60">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">{m.descrizione || "—"}</span>
          {m.is_spesa_fissa && <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#FC82ED]/10 text-[#b84fae]">Spesa fissa</span>}
        </div>
        <div className="text-xs text-[var(--muted)] truncate">{[m.fornitore, m.categoria].filter(Boolean).join(" · ") || "—"}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-bold mono text-sm">{euro(m.importo)}</div>
        <div className={`text-[11px] ${giorniCls(m.giorni)}`}>{fmtDate(m.scadenza)} {m.giorni != null && `· ${giorniLabel(m.giorni)}`}</div>
      </div>
      {right}
    </div>
  );
}

function Bucket({ title, color, items, render, testid }) {
  if (!items || !items.length) return null;
  return (
    <div className="card-immo overflow-hidden" data-testid={testid}>
      <div className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wide ${color}`}>{title} · {items.length}</div>
      <div>{items.map(render)}</div>
    </div>
  );
}

// ---------- DASHBOARD ----------
function DigestOggiCard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finanza/digest-oggi").then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false) return null;
  if (!d) return <div className="skeleton h-24 rounded-xl" />;
  const Sec = ({ title, rows, cols, keys, accent }) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="mb-3 last:mb-0">
        <div className={`text-[11px] font-semibold uppercase tracking-wide mb-1.5 ${accent || "text-[var(--muted)]"}`}>{title} · {rows.length}</div>
        <div className="rounded-lg border border-[var(--border)] divide-y divide-slate-100">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="truncate">{r[keys[0]]}{r[keys[1]] ? <span className="text-[var(--muted)]"> · {r[keys[1]]}</span> : null}</span>
              <span className="shrink-0 flex items-center gap-3"><span className="text-xs text-[var(--muted)] mono">{r.scadenza}</span><span className="font-semibold">{euro(r.importo)}</span></span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  const empty = !d.has_content;
  return (
    <div className="card-immo p-5" data-testid="finance-digest-oggi">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-base">Riepilogo finanza · {d.oggi_label}</h3>
        <span className="text-[11px] text-[var(--muted)]">anche via email alle 07:45</span>
      </div>
      {empty && <p className="text-sm text-[var(--muted)]">Nessuna scadenza finanziaria per oggi. 🎉</p>}
      <Sec title="Debiti scaduti" rows={d.deb_scaduti} keys={["descrizione", "fornitore"]} accent="text-red-600" />
      <Sec title="Debiti in scadenza oggi" rows={d.deb_oggi} keys={["descrizione", "fornitore"]} accent="text-amber-600" />
      <Sec title="Debiti prossimi 7 giorni" rows={d.deb_7} keys={["descrizione", "fornitore"]} />
      <Sec title="Incassi scaduti da riscuotere" rows={d.inc_scaduti} keys={["titolo", "cliente"]} accent="text-red-600" />
      <Sec title="Incassi attesi (7 giorni)" rows={d.inc_prossimi} keys={["titolo", "cliente"]} accent="text-emerald-600" />
      {d.over && d.over.length > 0 && (
        <div className="mb-0">
          <div className="text-[11px] font-semibold uppercase tracking-wide mb-1.5 text-red-600">Categorie oltre il tetto · {d.over.length}</div>
          <div className="flex flex-wrap gap-1.5">
            {d.over.map((o) => <span key={o.categoria} className="text-[11px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{o.categoria}: {euro(o.speso)}/{euro(o.tetto)}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finanza/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!d) return <div className="skeleton h-40 rounded-xl" />;
  const sf = d.spese_fisse_mese || {};
  const downloadPdf = async () => {
    try {
      const res = await api.get("/finanza/digest/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "digest_finanza.pdf"; a.click();
      URL.revokeObjectURL(url); toast.success("PDF del digest generato");
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button data-testid="dash-digest-pdf-btn" onClick={downloadPdf} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all"><Download size={15} /> Scarica PDF digest</button>
      </div>
      <DigestOggiCard />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-spese-mese" label="Spese del mese" value={euro(d.spese_mese)} icon={Wallet} />
        <KpiCard testid="kpi-da-pagare" label="Da pagare (scaduto)" value={euro(d.uscite_scadute)} accent icon={AlertTriangle} sub={`Programmato ${euro(d.uscite_programmate)}`} />
        <KpiCard testid="kpi-da-incassare" label="Da incassare" value={euro(d.provvigioni_da_incassare)} icon={TrendingUp} />
        <KpiCard testid="kpi-saldo" label="Saldo previsionale" value={euro(d.saldo_previsionale)} icon={ShieldCheck} />
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-immo p-5" data-testid="dash-spese-fisse-box">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--muted)]">Spese fisse del mese</span>
            <Repeat size={16} className="text-[#FC82ED]" />
          </div>
          <div className="font-display text-2xl font-extrabold">Pagate {sf.pagate || 0} di {sf.totale || 0}</div>
          <div className="text-xs text-[var(--muted)] mt-1">Ancora da pagare: <span className="font-semibold text-amber-600">{euro(sf.da_pagare_importo)}</span> · Pagato {euro(sf.importo_pagato)} su {euro(sf.importo_totale)}</div>
        </div>
        {d.budget_incidenza && (
          <div className="card-immo p-5" data-testid="dash-budget-incidenza">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-[var(--muted)]">Incidenza costi variabili</span>
              <Target size={16} className={d.budget_incidenza.over ? "text-red-500" : "text-[#FC82ED]"} />
            </div>
            <div className="flex items-end justify-between">
              <div className={`font-display text-2xl font-extrabold ${d.budget_incidenza.over ? "text-red-600" : ""}`}>{pctIt(d.budget_incidenza.incidenza || 0, 1)}</div>
              <div className="text-xs text-[var(--muted)]">{d.budget_incidenza.soglia > 0 ? `soglia ${pctIt(d.budget_incidenza.soglia, 0)}` : "nessuna soglia impostata"}</div>
            </div>
            <div className="relative h-2 bg-slate-100 rounded-full mt-2 overflow-hidden">
              <div className={`h-full ${d.budget_incidenza.over ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(d.budget_incidenza.incidenza || 0, 100)}%` }} />
              {d.budget_incidenza.soglia > 0 && <div className="absolute top-[-2px] bottom-[-2px] w-0.5 bg-slate-700" style={{ left: `${Math.min(d.budget_incidenza.soglia, 100)}%` }} title="Soglia" />}
            </div>
            <div className="text-xs text-[var(--muted)] mt-1.5">Variabili {euro(d.budget_incidenza.var_mese)} su {euro(d.budget_incidenza.totale_mese)} del mese{d.budget_incidenza.over ? " · ⚠ soglia superata" : ""}</div>
            {d.budget_incidenza.categorie_over && d.budget_incidenza.categorie_over.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid="dash-cat-over">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-red-600 mb-1.5">Categorie oltre il tetto</div>
                <div className="flex flex-wrap gap-1.5">
                  {d.budget_incidenza.categorie_over.map((c) => (
                    <span key={c.categoria} className="text-[11px] px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">{c.categoria}: {euro(c.speso)}/{euro(c.tetto)}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="card-immo p-5">
        <div className="text-sm font-semibold mb-3">Entrate vs Uscite (ultimi 6 mesi)</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={d.chart || []}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="mese" fontSize={11} /><YAxis fontSize={11} />
            <Tooltip formatter={(v) => euro(v)} /><Legend />
            <Bar dataKey="entrate" name="Entrate" fill="#34d399" radius={[4, 4, 0, 0]} />
            <Bar dataKey="uscite" name="Uscite" fill="#FC82ED" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------- DA PAGARE ----------
function DaPagare({ onChange }) {
  const [d, setD] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [paga, setPaga] = useState(null);
  const load = () => api.get("/finanza/da-pagare").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="skeleton h-40 rounded-xl" />;
  const refresh = () => { load(); onChange && onChange(); };
  const rowActions = (m) => (
    <div className="flex gap-1.5 shrink-0 items-center">
      <AllegatoBtn fid={m.fattura_file_id} label="Fattura" />
      <button data-testid={`paga-btn-${m.id}`} onClick={() => setPaga(m)} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 flex items-center gap-1"><Check size={13} /> Segna pagato</button>
      {!m.is_spesa_fissa && <button data-testid={`del-mov-${m.id}`} onClick={() => delMov(m.id, refresh)} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-4 text-sm">
          <span>Totale da pagare: <b className="mono">{euro(d.totali.da_pagare)}</b></span>
          <span className="text-red-600">Scaduto: <b className="mono">{euro(d.totali.scaduto)}</b></span>
          <span>Questo mese: <b className="mono">{euro(d.totali.mese)}</b></span>
        </div>
        <button data-testid="nuovo-debito-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm hover:bg-[#e06dd2]"><Plus size={15} /> Nuovo debito</button>
      </div>
      <Bucket testid="bucket-scaduti" title="Scaduti" color="bg-red-50 text-red-700" items={d.scaduti} render={(m) => <MovRow key={m.id} m={m} right={rowActions(m)} />} />
      <Bucket testid="bucket-entro7" title="Entro 7 giorni" color="bg-amber-50 text-amber-700" items={d.entro_7} render={(m) => <MovRow key={m.id} m={m} right={rowActions(m)} />} />
      <Bucket testid="bucket-mese" title="Questo mese" color="bg-slate-50 text-slate-600" items={d.questo_mese} render={(m) => <MovRow key={m.id} m={m} right={rowActions(m)} />} />
      <Bucket testid="bucket-avanti" title="Più avanti" color="bg-slate-50 text-slate-500" items={d.piu_avanti} render={(m) => <MovRow key={m.id} m={m} right={rowActions(m)} />} />
      {!d.scaduti.length && !d.entro_7.length && !d.questo_mese.length && !d.piu_avanti.length &&
        <div className="card-immo p-10 text-center text-[var(--muted)] text-sm">Nessun debito in sospeso. Tutto pagato! 🎉</div>}
      {showNew && <DebitoModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}
      {paga && <PagaModal m={paga} onClose={() => setPaga(null)} onSaved={() => { setPaga(null); refresh(); }} />}
    </div>
  );
}

async function delMov(id, cb) {
  if (!window.confirm("Eliminare questa voce?")) return;
  try { await api.delete(`/finanza/movimenti/${id}`); toast.success("Eliminato"); cb && cb(); }
  catch (e) { toast.error(apiError(e)); }
}

function DebitoModal({ onClose, onSaved }) {
  const cats = useCats();
  const [f, setF] = useState({ descrizione: "", fornitore: "", categoria: "Altro", importo: "", scadenza: oggi(), note: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const { errors, clear, applyServer, summary } = useFormErrors();
  const sa = useSpesaAppr();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };
  const save = async () => {
    setSaving(true);
    try { const r = await api.post("/finanza/debiti", { ...f, importo: parseFloat(f.importo) || 0, ...sa.payload }); await uploadAllegato(r.data.id, file, "fattura"); toast.success("Debito creato"); onSaved(); }
    catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Nuovo debito" onClose={onClose} onSave={save} saving={saving || !sa.ok(parseFloat(f.importo) || 0, f.fornitore)} testid="debito-modal" summary={summary}>
      <Field label="Descrizione" testid="deb-descrizione" required value={f.descrizione} onChange={set("descrizione")} error={errors.descrizione} />
      <Row>
        <Field label="Fornitore" testid="deb-fornitore" value={f.fornitore} onChange={set("fornitore")} />
        <Select label="Categoria" testid="deb-categoria" value={f.categoria} onChange={set("categoria")} opts={cats.length ? cats : ["Altro"]} />
      </Row>
      <Row>
        <Field label="Importo (€)" testid="deb-importo" type="euro" value={f.importo} onChange={set("importo")} error={errors.importo} />
        <Field label="Scadenza" testid="deb-scadenza" type="date" required value={f.scadenza} onChange={set("scadenza")} error={errors.scadenza} />
      </Row>
      <TextArea label="Note" testid="deb-note" rows={2} value={f.note} onChange={set("note")} />
      <AgenteSelect testid="deb-agente" value={f.agente_id} onChange={set("agente_id")} />
      <FileInput label="Allegato fattura" testid="deb-fattura" onPick={setFile} />
      {sa.campi(parseFloat(f.importo) || 0)}
    </Modal>
  );
}

function PagaModal({ m, onClose, onSaved }) {
  const [f, setF] = useState({ data_pagamento: oggi(), importo_pagato: String(m.importo ?? ""), metodo: "bonifico" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [soglia, setSoglia] = useState(0);
  useEffect(() => { api.get("/finanza/config").then((r) => setSoglia(r.data.ricevuta_obbligatoria_oltre || 0)).catch(() => {}); }, []);
  const { errors, clear, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };
  const imp = parseFloat(f.importo_pagato) || 0;
  const ricevutaMancante = soglia > 0 && imp > soglia && !file && !m.ricevuta_file_id;
  const save = async () => {
    if (ricevutaMancante) { toast.error(`Allega la ricevuta: obbligatoria per importi oltre ${euro(soglia)}.`); return; }
    setSaving(true);
    try {
      if (file) await uploadAllegato(m.id, file, "ricevuta");
      await api.post(`/finanza/movimenti/${m.id}/paga`, { ...f, importo_pagato: imp });
      toast.success("Segnato come pagato"); onSaved();
    } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={`Segna pagato · ${m.descrizione || ""}`} onClose={onClose} onSave={save} saving={saving} testid="paga-modal" saveLabel="Conferma pagamento" summary={summary}>
      <Row>
        <Field label="Data pagamento" testid="paga-data" type="date" value={f.data_pagamento} onChange={set("data_pagamento")} error={errors.data_pagamento} />
        <Field label="Importo pagato (€)" testid="paga-importo" type="euro" value={f.importo_pagato} onChange={set("importo_pagato")} error={errors.importo_pagato} />
      </Row>
      <Select label="Metodo" testid="paga-metodo" value={f.metodo} onChange={set("metodo")} opts={METODI} error={errors.metodo} />
      {soglia > 0 && imp > soglia && (
        <div data-testid="paga-soglia-warn" className={`text-xs px-3 py-2 rounded-lg border ${ricevutaMancante ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
          {ricevutaMancante ? `Oltre ${euro(soglia)} la ricevuta è obbligatoria: allega il file qui sotto.` : "Ricevuta allegata ✓"}
        </div>
      )}
      <FileInput label="Allegato ricevuta (facoltativo)" testid="paga-ricevuta" onPick={setFile} />
    </Modal>
  );
}

// ---------- SPESE ----------
function Spese({ onChange }) {
  const [mese, setMese] = useState(meseOggi());
  const [d, setD] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get(`/finanza/spese?mese=${mese}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [mese]);
  const refresh = () => { load(); onChange && onChange(); };
  if (!d) return <div className="skeleton h-40 rounded-xl" />;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <input data-testid="spese-mese" type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm" />
        <button data-testid="nuova-spesa-btn" onClick={() => setShowNew(true)} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm hover:bg-[#e06dd2]"><Plus size={15} /> Nuova spesa</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="spese-tot" label="Totale mese" value={euro(d.totale)} accent />
        <KpiCard testid="spese-fisse-tot" label="Spese fisse" value={euro(d.totale_fisse)} />
        <KpiCard testid="spese-var-tot" label="Spese variabili" value={euro(d.totale_variabili)} />
        <KpiCard testid="spese-prec" label="Mese precedente" value={euro(d.totale_prec)} sub={`${d.delta_prec >= 0 ? "+" : ""}${euro(d.delta_prec)}`} />
      </div>
      {!d.categorie.length && <div className="card-immo p-10 text-center text-[var(--muted)] text-sm">Nessuna spesa pagata in {mese}.</div>}
      {d.categorie.map((g) => (
        <div key={g.categoria} className="card-immo overflow-hidden" data-testid={`spesa-cat-${g.categoria}`}>
          <div className="px-4 py-2.5 flex items-center justify-between bg-slate-50">
            <span className="font-semibold text-sm">{g.categoria}</span>
            <span className="mono font-bold text-sm">{euro(g.totale)}{g.tetto != null && <span className={`ml-2 text-xs ${g.over ? "text-red-600" : "text-[var(--muted)]"}`}>/ tetto {euro(g.tetto)}{g.over ? " ⚠" : ""}</span>}</span>
          </div>
          {g.tetto != null && <div className="h-1.5 bg-slate-100"><div className={`h-full ${g.over ? "bg-red-500" : "bg-[#FC82ED]"}`} style={{ width: `${Math.min(g.perc || 0, 100)}%` }} /></div>}
          <div>{g.voci.map((m) => (
            <div key={m.id} data-testid={`spesa-row-${m.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[var(--border)] last:border-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="text-sm truncate">{m.descrizione}</span>{m.origine === "spesa_fissa" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#FC82ED]/10 text-[#b84fae]">FISSA</span>}</div>
                <div className="text-xs text-[var(--muted)]">{fmtDate(m.data_pagamento)} · {m.metodo || "-"}{m.fornitore ? ` · ${m.fornitore}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="mono font-semibold text-sm">{euro(m.importo_pagato ?? m.importo)}</span>
                <AllegatoBtn fid={m.fattura_file_id} label="Fattura" />
                <AllegatoBtn fid={m.ricevuta_file_id} label="Ricevuta" />
                <button data-testid={`annulla-pag-${m.id}`} onClick={() => annullaPag(m.id, refresh)} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-amber-600" title="Annulla pagamento"><Undo2 size={14} /></button>
              </div>
            </div>
          ))}</div>
        </div>
      ))}
      {showNew && <SpesaModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}
    </div>
  );
}

async function annullaPag(id, cb) {
  if (!window.confirm("Annullare il pagamento? La voce tornerà in Da pagare.")) return;
  try { await api.post(`/finanza/movimenti/${id}/annulla-pagamento`); toast.success("Pagamento annullato"); cb && cb(); }
  catch (e) { toast.error(apiError(e)); }
}

function SpesaModal({ onClose, onSaved }) {
  const cats = useCats();
  const [f, setF] = useState({ descrizione: "", fornitore: "", categoria: "Altro", importo: "", data_pagamento: oggi(), metodo: "bonifico", note: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const { errors, clear, applyServer, summary } = useFormErrors();
  const spa = useSpesaAppr();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };
  const save = async () => {
    const imp = parseFloat(f.importo) || 0;
    let soglia = 0;
    try { soglia = (await api.get("/finanza/config")).data.ricevuta_obbligatoria_oltre || 0; } catch {}
    if (soglia > 0 && imp > soglia && !file) { toast.error(`Allega fattura/ricevuta: obbligatoria per importi oltre ${euro(soglia)}.`); return; }
    setSaving(true);
    try { const r = await api.post("/finanza/spese", { ...f, importo: imp, ...spa.payload }); if (file) await uploadAllegato(r.data.id, file, "fattura"); toast.success("Spesa registrata"); onSaved(); }
    catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Nuova spesa (pagata)" onClose={onClose} onSave={save} saving={saving || !spa.ok(parseFloat(f.importo) || 0, f.fornitore)} testid="spesa-modal" summary={summary}>
      <Field label="Descrizione" testid="sp-descrizione" required value={f.descrizione} onChange={set("descrizione")} error={errors.descrizione} />
      <Row>
        <Field label="Fornitore" testid="sp-fornitore" value={f.fornitore} onChange={set("fornitore")} />
        <Select label="Categoria" testid="sp-categoria" value={f.categoria} onChange={set("categoria")} opts={cats.length ? cats : ["Altro"]} />
      </Row>
      <Row>
        <Field label="Importo (€)" testid="sp-importo" type="euro" value={f.importo} onChange={set("importo")} error={errors.importo} />
        <Field label="Data pagamento" testid="sp-data" type="date" value={f.data_pagamento} onChange={set("data_pagamento")} />
      </Row>
      <Select label="Metodo" testid="sp-metodo" value={f.metodo} onChange={set("metodo")} opts={METODI} error={errors.metodo} />
      {(f.categoria || "").toLowerCase().includes("marketing") && (
        <Select label="Canale (provenienza notizie)" testid="sp-canale" value={f.canale || ""} onChange={set("canale")}
          opts={[["", "— nessuno —"], ...PROVENIENZE.map((p) => [p, p])]} />
      )}
      <TextArea label="Note" testid="sp-note" rows={2} value={f.note} onChange={set("note")} />
      <AgenteSelect testid="sp-agente" value={f.agente_id} onChange={set("agente_id")} />
      {spa.campi(parseFloat(f.importo) || 0)}
      <FileInput label="Allegato fattura/ricevuta" testid="sp-fattura" onPick={setFile} />
    </Modal>
  );
}

// ---------- SPESE FISSE ----------
const SF_STATO = { attiva: "bg-emerald-100 text-emerald-700", sospesa: "bg-amber-100 text-amber-700", terminata: "bg-slate-200 text-slate-500" };
function SpeseFisse({ onChange }) {
  const { isDirezione: puoGestire } = useAuth();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [storico, setStorico] = useState(null);
  const load = () => api.get("/finanza/spese-fisse").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChange && onChange(); };
  if (!d) return <div className="skeleton h-40 rounded-xl" />;
  const act = async (id, azione, data_fine) => {
    try { await api.post(`/finanza/spese-fisse/${id}/stato`, { azione, data_fine }); toast.success("Aggiornata"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm">
          <div data-testid="sf-totale-frase" className="font-display text-lg font-bold">Le spese fisse dell'ufficio sono <span className="mono text-[#FC82ED]">{euro(d.totale_mensile)}</span> al mese</div>
          <div className="text-xs text-[var(--muted)]">Tetto di riferimento · {euro(d.totale_annuo)} l'anno · non entra nei conti del mese: fanno fede solo i movimenti dell'estratto conto</div>
        </div>
        {!puoGestire && <span data-testid="sf-solo-soci" className="text-xs text-[var(--muted)]">Le spese fisse le gestiscono i Soci</span>}
        {puoGestire && <button data-testid="nuova-spesa-fissa-btn" onClick={() => setEdit({})} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm hover:bg-[#e06dd2]"><Plus size={15} /> Nuova spesa fissa</button>}
      </div>
      {!d.categorie.length && <div className="card-immo p-10 text-center text-[var(--muted)] text-sm">Nessuna spesa fissa. Aggiungi affitto, utenze, software…</div>}
      {d.categorie.map((g) => (
        <div key={g.categoria} className="card-immo overflow-hidden" data-testid={`sf-cat-${g.categoria}`}>
          <div className="px-4 py-2.5 flex items-center justify-between bg-slate-50"><span className="font-semibold text-sm">{g.categoria}</span><span className="mono text-xs text-[var(--muted)]">{euro(g.quota_mensile)}/mese</span></div>
          {g.voci.map((sf) => (
            <div key={sf.id} data-testid={`sf-row-${sf.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border)] last:border-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm truncate">{sf.descrizione}</span>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${SF_STATO[sf.stato] || ""}`}>{sf.stato}</span>
                </div>
                <div className="text-xs text-[var(--muted)]">{sf.frequenza} · prossima {fmtDate(sf.prossima_scadenza)}{sf.data_inizio ? ` · attiva dal ${fmtDate(sf.data_inizio)}` : ""}{sf.data_fine ? ` al ${fmtDate(sf.data_fine)}` : ""}{sf.fornitore ? ` · ${sf.fornitore}` : ""}{sf.storico_count ? ` · ${sf.storico_count} pagamenti` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="mono font-bold text-sm">{euro(sf.importo)}</span>
                <span className="text-[11px] text-[var(--muted)] mono">({euro(sf.quota_mensile)}/m)</span>
                <button data-testid={`sf-storico-${sf.id}`} onClick={() => setStorico(sf)} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-[#FC82ED]" title="Storico"><History size={14} /></button>
                {puoGestire && <>
                <button data-testid={`sf-edit-${sf.id}`} onClick={() => setEdit(sf)} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-[#FC82ED]"><Pencil size={14} /></button>
                <DeleteBtn tipo="spesa_fissa" id={sf.id} label={`${sf.descrizione} – ${euro(sf.importo)}`} options={[["prossima", "Elimina solo la prossima scadenza"], ["tutta", "Elimina la spesa fissa (anche tutte le scadenze future)"]]} onDone={onChange} />
                {sf.stato === "attiva" && <button data-testid={`sf-sospendi-${sf.id}`} onClick={() => act(sf.id, "sospendi")} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-amber-600" title="Sospendi"><Pause size={14} /></button>}
                {sf.stato === "sospesa" && <button data-testid={`sf-riattiva-${sf.id}`} onClick={() => act(sf.id, "riattiva")} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-emerald-600" title="Riattiva"><Play size={14} /></button>}
                {sf.stato !== "terminata" && <button data-testid={`sf-termina-${sf.id}`} onClick={() => { const df = window.prompt("Data di fine (YYYY-MM-DD)", oggi()); if (df) act(sf.id, "termina", df); }} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-red-600" title="Termina"><Square size={14} /></button>}
                </>}
              </div>
            </div>
          ))}
        </div>
      ))}
      {edit && <SpesaFissaModal sf={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} />}
      {storico && <StoricoModal sf={storico} onClose={() => setStorico(null)} />}
    </div>
  );
}

function SpesaFissaModal({ sf, onClose, onSaved }) {
  const cats = useCats();
  const [f, setF] = useState(sf ? {
    descrizione: sf.descrizione || "", fornitore: sf.fornitore || "", categoria: sf.categoria || "Altro",
    importo: String(sf.importo ?? ""), frequenza: sf.frequenza || "mensile", giorno_scadenza: String(sf.giorno_scadenza || 1), note: sf.note || "", agente_id: sf.agente_id || "", dal_mese: meseOggi(), ripartisci: sf.ripartisci !== false,
  } : { descrizione: "", fornitore: "", categoria: "Altro", importo: "", frequenza: "mensile", giorno_scadenza: "1", data_inizio: oggi(), note: "", ripartisci: true });
  const [saving, setSaving] = useState(false);
  const { errors, clear, applyServer, summary } = useFormErrors();
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); clear(k); };
  const save = async () => {
    setSaving(true);
    const payload = { ...f, importo: parseFloat(f.importo) || 0, giorno_scadenza: parseInt(f.giorno_scadenza) || 1 };
    try {
      if (sf) await api.put(`/finanza/spese-fisse/${sf.id}`, payload);
      else await api.post("/finanza/spese-fisse", payload);
      toast.success(sf ? "Spesa fissa aggiornata" : "Spesa fissa creata"); onSaved();
    } catch (e) { if (applyServer(e)) return; toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={sf ? "Modifica spesa fissa" : "Nuova spesa fissa"} onClose={onClose} onSave={save} saving={saving} testid="spesa-fissa-modal" summary={summary} scheda value={f} onRestore={setF}>
      {sf && <p className="text-xs text-[var(--muted)] -mt-1">La modifica dell'importo vale dal mese scelto in poi; i mesi passati e le voci già pagate restano invariati.</p>}
      <Field label="Descrizione" testid="sf-descrizione" required value={f.descrizione} onChange={set("descrizione")} error={errors.descrizione} />
      <Row>
        <Field label="Fornitore" testid="sf-fornitore" value={f.fornitore} onChange={set("fornitore")} />
        <Select label="Categoria" testid="sf-categoria" value={f.categoria} onChange={set("categoria")} opts={cats.length ? cats : ["Altro"]} />
      </Row>
      <Row>
        <Field label="Importo (€)" testid="sf-importo" type="euro" value={f.importo} onChange={set("importo")} error={errors.importo} />
        <Select label="Frequenza" testid="sf-frequenza" value={f.frequenza} onChange={set("frequenza")} opts={FREQ} error={errors.frequenza} />
      </Row>
      {f.frequenza !== "mensile" && (
        <label data-testid="sf-ripartisci" className="flex items-start gap-2.5 p-3 rounded-xl bg-[#F7F7F8] border border-[var(--border)] cursor-pointer">
          <input type="checkbox" data-testid="sf-ripartisci-checkbox" checked={f.ripartisci} onChange={(e) => setF({ ...f, ripartisci: e.target.checked })} className="w-5 h-5 mt-0.5 accent-[#FC82ED] shrink-0" />
          <span className="text-sm"><b>Ripartisci nei mesi</b><span className="block text-xs text-[var(--muted)]">Ogni mese conta la quota ({euro((parseFloat(f.importo) || 0) / ({ bimestrale: 2, trimestrale: 3, semestrale: 6, annuale: 12 }[f.frequenza] || 1))}/mese); il pagamento intero resta un promemoria nel mese di scadenza.</span></span>
        </label>
      )}
      <Row>
        <Field label="Giorno di scadenza" testid="sf-giorno" type="number" value={f.giorno_scadenza} onChange={set("giorno_scadenza")} />
        {!sf && <Field label="Data inizio" testid="sf-inizio" type="date" value={f.data_inizio} onChange={set("data_inizio")} />}
        {sf && <Field label="Nuovo importo valido dal mese" testid="sf-dal-mese" type="month" value={f.dal_mese} onChange={set("dal_mese")} />}
      </Row>
      <TextArea label="Note" testid="sf-note" rows={2} value={f.note} onChange={set("note")} />
      <AgenteSelect testid="sf-agente" value={f.agente_id} onChange={set("agente_id")} />
    </Modal>
  );
}

function StoricoModal({ sf, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/finanza/spese-fisse/${sf.id}/storico`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [sf.id]);
  return (
    <Modal title={`Storico pagamenti · ${sf.descrizione}`} onClose={onClose} onSave={onClose} saveLabel="Chiudi" hideCancel testid="sf-storico-modal">
      {!rows ? <div className="skeleton h-24 rounded-xl" /> : !rows.length ? <p className="text-sm text-[var(--muted)]">Nessun pagamento registrato.</p> :
        rows.map((m) => (
          <div key={m.id} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0 text-sm">
            <span>{fmtDate(m.data_pagamento)} · {m.metodo}</span><span className="mono font-semibold">{euro(m.importo_pagato ?? m.importo)}</span>
          </div>
        ))}
    </Modal>
  );
}

// ---------- INCASSI ----------
function Incassi({ onChange }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/finanza/incassi").then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChange && onChange(); };
  if (!d) return <div className="skeleton h-40 rounded-xl" />;
  const di = d.da_incassare;
  const incassa = async (id) => { try { await api.post(`/finanza/provvigioni/${id}/incassa`, { data_incasso: oggi() }); toast.success("Incassato"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const annulla = async (id) => { if (!window.confirm("Annullare l'incasso?")) return; try { await api.post(`/finanza/provvigioni/${id}/annulla-incasso`); toast.success("Annullato"); refresh(); } catch (e) { toast.error(apiError(e)); } };
  const prow = (p, paid) => (
    <div key={p.id} data-testid={`incasso-row-${p.id}`} className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[var(--border)] last:border-0">
      <div className="min-w-0">
        <div className="font-semibold text-sm truncate">{p.immobile_titolo || p.pratica || "Provvigione"}</div>
        <div className="text-xs text-[var(--muted)]">{p.agente_nome || "—"}{p.cliente ? ` · ${p.cliente}` : ""} · {paid ? `incassato ${fmtDate(p.data_incasso)}` : fmtDate(p.scadenza)}{!paid && p.giorni != null ? ` · ${giorniLabel(p.giorni)}` : ""}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="mono font-bold text-sm">{euro(p.totale || p.importo)}</span>
        {paid
          ? <button data-testid={`annulla-incasso-${p.id}`} onClick={() => annulla(p.id)} className="p-1.5 rounded-lg border border-[var(--border)] text-slate-500 hover:text-amber-600" title="Annulla incasso"><Undo2 size={14} /></button>
          : <button data-testid={`incassa-${p.id}`} onClick={() => incassa(p.id)} className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 flex items-center gap-1"><Check size={13} /> Segna incassato</button>}
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <KpiCard testid="inc-da-incassare" label="Da incassare" value={euro(d.totali.da_incassare)} accent />
        <KpiCard testid="inc-mese" label="Incassato questo mese" value={euro(d.totali.incassato_mese)} />
      </div>
      <h3 className="text-sm font-bold uppercase tracking-wide text-[var(--muted)]">Da incassare</h3>
      <Bucket testid="inc-scaduti" title="Scaduti" color="bg-red-50 text-red-700" items={di.scaduti} render={(p) => prow(p, false)} />
      <Bucket testid="inc-entro7" title="Entro 7 giorni" color="bg-amber-50 text-amber-700" items={di.entro_7} render={(p) => prow(p, false)} />
      <Bucket testid="inc-mese-b" title="Questo mese" color="bg-slate-50 text-slate-600" items={di.questo_mese} render={(p) => prow(p, false)} />
      <Bucket testid="inc-avanti" title="Più avanti" color="bg-slate-50 text-slate-500" items={di.piu_avanti} render={(p) => prow(p, false)} />
      {!di.scaduti.length && !di.entro_7.length && !di.questo_mese.length && !di.piu_avanti.length && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessuna provvigione da incassare.</div>}
      <h3 className="text-sm font-bold uppercase tracking-wide text-[var(--muted)] pt-2">Incassati</h3>
      {!d.incassati.length && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessun incasso registrato.</div>}
      {d.incassati.map((g) => (
        <div key={g.mese} className="card-immo overflow-hidden" data-testid={`inc-mese-${g.mese}`}>
          <div className="px-4 py-2.5 flex items-center justify-between bg-slate-50"><span className="font-semibold text-sm">{g.mese}</span><span className="mono font-bold text-sm">{euro(g.totale)}</span></div>
          <div>{g.voci.map((p) => prow(p, true))}</div>
        </div>
      ))}
      <ReportAgenti />
    </div>
  );
}

function ReportAgenti() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/finanza/report-agenti").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d || !d.agenti.length) return null;
  return (
    <div className="card-immo overflow-hidden mt-2" data-testid="report-agenti">
      <div className="px-4 py-2.5 bg-slate-50 font-semibold text-sm">Provvigioni per agente</div>
      <div className="overflow-x-auto"><table className="w-full text-sm">
        <thead><tr className="text-[var(--muted)] text-xs"><th className="text-left px-4 py-2">Agente</th><th className="text-right px-4 py-2">N.</th><th className="text-right px-4 py-2">Totale</th><th className="text-right px-4 py-2">Incassato</th><th className="text-right px-4 py-2">Da incassare</th></tr></thead>
        <tbody>{d.agenti.map((a) => (
          <tr key={a.owner_agent_id} className="border-t border-[var(--border)]"><td className="px-4 py-2">{a.agente_nome}</td><td className="text-right px-4 py-2">{a.provvigioni}</td><td className="text-right px-4 py-2 mono">{euro(a.totale)}</td><td className="text-right px-4 py-2 mono text-emerald-600">{euro(a.incassato)}</td><td className="text-right px-4 py-2 mono text-amber-600">{euro(a.da_incassare)}</td></tr>
        ))}</tbody>
      </table></div>
    </div>
  );
}

// ---------- IMPOSTAZIONI ----------
function Impostazioni() {
  const { isDirezione, isSegreteria } = useAuth();
  const [cats, setCats] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [budget, setBudget] = useState(null);
  const [report, setReport] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [savingB, setSavingB] = useState(false);
  useEffect(() => {
    api.get("/finanza/categorie").then((r) => setCats(r.data.categorie || []));
    api.get("/finanza/budget").then((r) => setBudget(r.data));
    api.get("/finanza/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/settings/report").then((r) => setReport(r.data)).catch(() => setReport({ email: "" }));
  }, []);
  const saveCats = async (list) => { try { const r = await api.put("/finanza/categorie", { categorie: list }); setCats(r.data.categorie); toast.success("Categorie salvate"); } catch (e) { toast.error(apiError(e)); } };
  const addCat = () => { const v = newCat.trim(); if (!v) return; saveCats([...(cats || []), v]); setNewCat(""); };
  const delCat = (c) => saveCats((cats || []).filter((x) => x !== c));
  const saveBudget = async () => {
    setSavingB(true);
    const categorie = {};
    (budget.categorie || []).forEach((c) => { if (c.tetto != null && c.tetto !== "") categorie[c.categoria] = { tetto: parseFloat(c.tetto) || 0, soglia_pct: c.soglia_pct || null }; });
    try { const r = await api.put("/finanza/budget", { soglia_incidenza_pct: budget.soglia_incidenza_pct || 0, categorie }); setBudget(r.data); toast.success("Budget salvato"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingB(false); }
  };
  const setTetto = (cat, v) => setBudget({ ...budget, categorie: budget.categorie.map((c) => c.categoria === cat ? { ...c, tetto: v } : c) });
  const saveReport = async () => { try { await api.put("/settings/report", report); toast.success("Email report salvata"); } catch (e) { toast.error(apiError(e)); } };
  const saveCfg = async () => { try { const r = await api.put("/finanza/config", { preavviso_debiti: parseInt(cfg.preavviso_debiti) || 0, preavviso_incassi: parseInt(cfg.preavviso_incassi) || 0, ricevuta_obbligatoria_oltre: parseFloat(cfg.ricevuta_obbligatoria_oltre) || 0, alert_saldo_soglia: parseFloat(cfg.alert_saldo_soglia) || 0, quota_collab_default: parseFloat(cfg.quota_collab_default) || 0, provv_media_default: parseFloat(cfg.provv_media_default) || 5000, obiettivo_utile_mese: parseFloat(cfg.obiettivo_utile_mese) || 0, probabile_pct: cfg.probabile_pct === "" || cfg.probabile_pct == null ? 70 : parseFloat(cfg.probabile_pct), ...(isDirezione ? { numero_soci: parseInt(cfg.numero_soci) || 3 } : {}), ...(isDirezione && cfg.contabilita_dal ? { contabilita_dal: cfg.contabilita_dal } : {}), ...(isDirezione && cfg.ricorrenti_dal ? { ricorrenti_dal: cfg.ricorrenti_dal } : {}) }); setCfg(r.data); toast.success("Impostazioni salvate"); } catch (e) { toast.error(apiError(e)); } };
  if (!cats || !budget) return <div className="skeleton h-40 rounded-xl" />;
  const budgetCats = Array.from(new Set([...(budget.categorie || []).map((c) => c.categoria), ...cats]))
    .map((name) => (budget.categorie || []).find((c) => c.categoria === name) || { categoria: name, tetto: null });
  return (
    <div className="space-y-5">
      <div className="card-immo p-5" data-testid="imp-categorie">
        <div className="flex items-center gap-2 mb-3"><Settings size={16} className="text-[#FC82ED]" /><h3 className="font-semibold">Categorie di spesa</h3></div>
        <div className="flex flex-wrap gap-2 mb-3">
          {cats.map((c) => (
            <span key={c} data-testid={`cat-chip-${c}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-sm">{c}
              <button data-testid={`cat-del-${c}`} onClick={() => delCat(c)} className="text-slate-400 hover:text-red-600"><Trash2 size={12} /></button></span>
          ))}
        </div>
        <div className="flex gap-2">
          <input data-testid="cat-new-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} placeholder="Nuova categoria" className="flex-1 px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm" />
          <button data-testid="cat-add-btn" onClick={addCat} className="px-3.5 py-2 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm">Aggiungi</button>
        </div>
      </div>
      <div className="card-immo p-5" data-testid="imp-budget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Target size={16} className="text-[#FC82ED]" /><h3 className="font-semibold">Budget mensile per categoria</h3></div>
          <button data-testid="budget-save-btn" onClick={saveBudget} disabled={savingB} className="px-3.5 py-2 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-60">Salva budget</button>
        </div>
        <div className="space-y-2">
          {budgetCats.map((c) => (
            <div key={c.categoria} className="flex items-center justify-between gap-3">
              <span className="text-sm">{c.categoria}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--muted)]">tetto €</span>
                <EuroInput testid={`budget-tetto-${c.categoria}`} value={c.tetto ?? ""} onChange={(e) => setTetto(c.categoria, e.target.value)} className="w-28 px-2 py-1.5 rounded-lg bg-white border border-[var(--border)] text-sm text-right" />
              </div>
            </div>
          ))}
        </div>
      </div>
      {cfg && (
        <div className="card-immo p-5" data-testid="imp-config">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Settings size={16} className="text-[#FC82ED]" /><h3 className="font-semibold">Promemoria e tracciabilità</h3></div>
            <button data-testid="config-save-btn" onClick={saveCfg} className="px-3.5 py-2 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm">Salva</button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="text-sm text-[var(--muted)]">Preavviso debiti (giorni)
              <input data-testid="cfg-preavviso-debiti" type="number" value={cfg.preavviso_debiti ?? 3} onChange={(e) => setCfg({ ...cfg, preavviso_debiti: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            <label className="text-sm text-[var(--muted)]">Preavviso incassi (giorni)
              <input data-testid="cfg-preavviso-incassi" type="number" value={cfg.preavviso_incassi ?? 3} onChange={(e) => setCfg({ ...cfg, preavviso_incassi: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            <label className="text-sm text-[var(--muted)]">Ricevuta obbligatoria oltre (€)
              <EuroInput testid="cfg-ricevuta-soglia" value={cfg.ricevuta_obbligatoria_oltre ?? 0} onChange={(e) => setCfg({ ...cfg, ricevuta_obbligatoria_oltre: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            <label className="text-sm text-[var(--muted)]">Avvisa se saldo settimana sotto (€)
              <EuroInput testid="cfg-alert-soglia" value={cfg.alert_saldo_soglia ?? 0} onChange={(e) => setCfg({ ...cfg, alert_saldo_soglia: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            {isDirezione && <label className="text-sm text-[var(--muted)]">Numero dei soci (quota per socio in Prossimi incassi)
              <input data-testid="cfg-numero-soci" type="number" min="1" value={cfg.numero_soci ?? 3} onChange={(e) => setCfg({ ...cfg, numero_soci: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>}
            <label className="text-sm text-[var(--muted)]">Pareggio: quota collaboratori se mancano dati (%)
              <input data-testid="cfg-quota-collab" type="number" value={cfg.quota_collab_default ?? 30} onChange={(e) => setCfg({ ...cfg, quota_collab_default: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            <label className="text-sm text-[var(--muted)]">Provvigione media per transazione (un solo lato: venditore OPPURE acquirente) se mancano dati (€)
              <EuroInput testid="cfg-provv-media" value={cfg.provv_media_default ?? 5000} onChange={(e) => setCfg({ ...cfg, provv_media_default: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            <label className="text-sm text-[var(--muted)]">Obiettivo utile del mese (€)
              <EuroInput testid="cfg-obiettivo-utile" value={cfg.obiettivo_utile_mese ?? 10000} onChange={(e) => setCfg({ ...cfg, obiettivo_utile_mese: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            <label className="text-sm text-[var(--muted)]">Provvigioni probabili (proposte accettate): % conteggiata
              <input data-testid="cfg-probabile-pct" type="number" min="0" max="100" value={cfg.probabile_pct ?? 70} onChange={(e) => setCfg({ ...cfg, probabile_pct: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>
            {isDirezione && <label className="text-sm text-[var(--muted)]">Contabilità nel CRM dal mese
              <input data-testid="cfg-contabilita-dal" type="month" value={cfg.contabilita_dal || "2026-10"} onChange={(e) => setCfg({ ...cfg, contabilita_dal: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>}
            {false && isDirezione && <label className="text-sm text-[var(--muted)]">Spese fisse ricorrenti generate dal mese
              <input data-testid="cfg-ricorrenti-dal" type="month" value={cfg.ricorrenti_dal || "2026-10"} onChange={(e) => setCfg({ ...cfg, ricorrenti_dal: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm text-[#111111]" /></label>}
          </div>
          {(isDirezione || isSegreteria) && <CassaBanca />}
          {(isDirezione || isSegreteria) && <MovimentiPrevisti />}
          {isDirezione && <StoricoPulizia />}
          {isDirezione && <RecuperoRipetute />}
          <p className="text-xs text-[var(--muted)] mt-2">Ricevuta: 0 = disattivato, obbligatoria quando l'importo pagato supera la soglia. Avviso saldo: 0 = avvisa quando il saldo della settimana va sotto zero; es. 2000 = avvisa quando scende sotto 2.000 €.</p>
        </div>
      )}
      {report && (
        <div className="card-immo p-5" data-testid="imp-report">
          <h3 className="font-semibold mb-3">Email Direzione (report e promemoria)</h3>
          <div className="flex gap-2">
            <input data-testid="report-email" value={report.email || ""} onChange={(e) => setReport({ ...report, email: e.target.value })} placeholder="email@azienda.it" className="flex-1 px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-sm" />
            <button data-testid="report-save-btn" onClick={saveReport} className="px-3.5 py-2 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm">Salva</button>
          </div>
          <p className="text-xs text-[var(--muted)] mt-2">Promemoria debiti scaduti/in scadenza (3 giorni), report mensile e avvisi budget vengono inviati qui.</p>
        </div>
      )}
    </div>
  );
}

// ---------- EXPORT ----------
function ExportModal({ onClose }) {
  const [f, setF] = useState({ dataset: "spese", format: "xlsx", mese: meseOggi(), anno: "", trimestre: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const download = async () => {
    try {
      const params = new URLSearchParams();
      params.set("dataset", f.dataset); params.set("format", f.format);
      if (f.mese) params.set("mese", f.mese);
      if (f.anno) params.set("anno", f.anno);
      if (f.trimestre && f.anno) params.set("trimestre", f.trimestre);
      const res = await api.get(`/finanza/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `finanza_${f.mese || f.anno || "tutto"}.${f.format === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url); toast.success("Export generato"); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal title="Esporta contabilità" onClose={onClose} onSave={download} saveLabel="Scarica" testid="export-modal">
      <Select label="Scheda" testid="exp-dataset" value={f.dataset} onChange={set("dataset")} opts={[["spese", "Spese"], ["da-pagare", "Da pagare"], ["incassi", "Incassi"], ["spese-fisse", "Spese fisse (definizioni)"]]} />
      <Select label="Formato" testid="exp-format" value={f.format} onChange={set("format")} opts={[["xlsx", "Excel (XLSX, tutte le schede)"], ["csv", "CSV (scheda selezionata)"]]} />
      <Row>
        <Field label="Mese (YYYY-MM)" testid="exp-mese" type="month" value={f.mese} onChange={set("mese")} />
        <Field label="Anno (YYYY)" testid="exp-anno" value={f.anno} onChange={set("anno")} />
      </Row>
      <p className="text-xs text-[var(--muted)]">Lascia il mese vuoto e usa l'anno (+ trimestre opzionale) per esportare periodi più ampi.</p>
    </Modal>
  );
}
